import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import fetchCustom from '../../../wp_helpers/fetchCustom';
import { useJobEdit } from '../context';
import { useTranslations } from '../../context/TranslationContext';
import { appendQueryParams, cloneData } from '../../../wp_helpers/helpers';
import useEvaluation from '../../Evaluation/hooks/useEvaluation';
import { useDeepCompareEffect } from '@hooks/useDeepCompareEffect';

const RequirementsTabContext = createContext(null);

export const RequirementsTabProvider = ({ children, aiJobSkillsRecommendation, onUpdate = () => { } }) => {
    const { t } = useTranslations();
    const { steps = [], config = {} } = useJobEdit();
    const initialDataRef = useRef(null);
    const [suggestedAttributesSet, setSuggestedAttributesSet] = useState({});

    const evaluationConfig = useEvaluation();
    const { evaluationCriteria, attributes, updateStore, loading } = evaluationConfig;
    const locale = config.locale || window.lang;

    useDeepCompareEffect(() => {
        updateStore({ steps });
    }, [steps]);

    const applyEvaluationTemplate = (data) => {
        updateStore({
            attributes: data.attributes,
            evaluationCriteria: data.evaluationCriteria,
        });
    };

    const init = async () => {
        updateStore({ loading: true });
        const url = appendQueryParams(config.get_requirements_section_data_url, { lang: locale });
        const response = await fetchCustom(url);
        if (!response.ok) {
            notification(global_i18n('ajax_fail_notification'));
            return;
        }
        const {
            attributes: _attributes,
            evaluation_criteria = [],
            evaluated_steps = {},
            rating_options = [],
            attributes_config = [],
            evaluation_templates = [],
        } = await response.json();

        updateStore({
            ratingOptions: rating_options,
            attributesConfig: attributes_config,
            evaluatedSteps: evaluated_steps,
            attributes: _attributes,
            evaluationCriteria: evaluation_criteria,
            loading: false,
            evaluationTemplatesOptions: evaluation_templates,
        });
        initialDataRef.current = cloneData({
            attributes: _attributes,
            evaluationCriteria: evaluation_criteria,
        });
    };

    useEffect(() => {
        init();

        window.aiJobSkillsRecommendation = (data) => {
            if (data) setSuggestedAttributesSet(data);
        };

        return () => {
            window.aiJobSkillsRecommendation = null;
        };
    }, []);

    useEffect(() => {
        // Don't propagate while the initial fetch is still in flight — the
        // attributes and evaluationCriteria are still at their default empty
        // values, and notifying the parent at this point would set the
        // baseline to "empty", causing the loaded data to look like a user
        // change when the fetch completes.
        if (loading) return;
        // Add the "unsaved-content" class if the attributes or evaluation criteria have changed
        // It will be use to get the data for legacy job edit view
        const initialAttributes = initialDataRef.current?.attributes || [];
        const initialEvaluationCriteria = initialDataRef.current?.evaluationCriteria || [];
        if (!_.isEqual(attributes, initialAttributes) || !_.isEqual(evaluationCriteria, initialEvaluationCriteria)) {
            document.body.classList.add('unsaved-content');
        }
        const requirementsAttributes = [];
        let error = false;
        Object.entries(attributes).forEach(([attr, values]) => {
            values.forEach(({ value }) => {
                if (!attr || !value) {
                    error = true;
                }
                requirementsAttributes.push(`${attr}:${value}`);
            });
        });
        let payload = {
            requirements_attributes: requirementsAttributes,
            evaluation_criteria: evaluationCriteria,
        }
        if (!error) {
            window.getRequirementsData = () => payload;
            onUpdate(payload, null);
        } else {
            notification(global_i18n('ajax_fail_notification'));
            onUpdate(payload, { 'error': global_i18n('ajax_fail_notification') });
        }
    }, [loading, attributes, evaluationCriteria, onUpdate]);

    return (
        <RequirementsTabContext.Provider
            value={{
                loading,
                locale,
                suggestedAttributesSet,
                setSuggestedAttributesSet,
                aiJobSkillsRecommendation,
                config,
                applyEvaluationTemplate,
                ...evaluationConfig,
            }}
        >
            {children}
        </RequirementsTabContext.Provider>
    );
};

RequirementsTabProvider.defaultProps = {
    config: {},
};

export const useRequirementsTab = () => {
    const context = useContext(RequirementsTabContext);
    if (context === null) {
        throw new Error('useRequirementsTab must be used within a RequirementsTabProvider');
    }
    return context;
};
